"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, loadTokens } from "@/lib/api";

/**
 * EPIC-013 STORY-1307 — closed-beta self-service virtual money reset.
 *
 * Renders alongside TestFundsBanner on /home. The two are mutually
 * exclusive by trigger: TestFundsBanner shows when balance == 0 (the
 * signup-grant path); VirtualResetBanner shows when balance is low
 * but non-zero (the user blew through their grant on bad bets and
 * wants to reset back to the target).
 *
 * Cooldown state is pre-fetched from /v1/me's `last_virtual_reset_at`
 * (added in this PR) so we can suppress the CTA mid-cooldown rather
 * than relying on the click-then-403 path. The 403 still serves as
 * defense-in-depth.
 */
const RESET_THRESHOLD_CENTS = BigInt(2_000);
// Mirrors apps/api/src/users/users.service.ts VIRTUAL_RESET_COOLDOWN_DAYS.
const RESET_COOLDOWN_DAYS = 30;
const COOLDOWN_MS = RESET_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

function cooldownEndsAt(lastResetAt: string | null | undefined): number | null {
  if (!lastResetAt) return null;
  const ms = Date.parse(lastResetAt);
  if (!Number.isFinite(ms)) return null;
  const ends = ms + COOLDOWN_MS;
  return ends > Date.now() ? ends : null;
}

export function VirtualResetBanner() {
  const { t } = useTranslation();
  const [cashCents, setCashCents] = useState<bigint | null>(null);
  const [cooldownEndsMs, setCooldownEndsMs] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void load();
    async function load() {
      if (!loadTokens()) return;
      try {
        const [wallets, profile] = await Promise.all([
          api.listMyWallets(),
          api.me(),
        ]);
        const cash = wallets.find((w) => w.kind === "cash");
        if (cancelled) return;
        setCashCents(cash ? BigInt(cash.balance_cents) : BigInt(0));
        setCooldownEndsMs(cooldownEndsAt(profile.last_virtual_reset_at));
      } catch {
        // silent — banner just won't show
      }
    }
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleReset() {
    if (!loadTokens()) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await api.requestVirtualReset();
      setCashCents(BigInt(res.balanceBeforeCents) + BigInt(res.mintAmountCents));
      setSuccess(
        t("virtualResetBanner.successTemplate", {
          days: RESET_COOLDOWN_DAYS,
        }),
      );
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("virtualResetBanner.errorFallback"),
      );
    } finally {
      setBusy(false);
    }
  }

  if (cashCents === null) return null;
  // Hide for users at zero (TestFundsBanner handles that case) and for
  // users with healthy balances. The backend also enforces both gates.
  if (cashCents <= BigInt(0) || cashCents >= RESET_THRESHOLD_CENTS) return null;
  // Suppress the banner during the cooldown window — surface re-emerges
  // on the day cooldown elapses.
  if (cooldownEndsMs !== null && success === null) return null;

  return (
    <div
      className="mb-4 flex flex-col gap-2 rounded-xl border p-4 sm:flex-row sm:items-center"
      style={{
        borderColor: "color-mix(in srgb, var(--color-brand) 30%, transparent)",
        background: "color-mix(in srgb, var(--color-brand) 5%, transparent)",
      }}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-[var(--color-text)]">
          {t("virtualResetBanner.title")}
        </p>
        <p className="mt-0.5 text-[11px] text-[var(--color-text2)]">
          {t("virtualResetBanner.body")}
        </p>
        {success && (
          <p
            className="mt-1 text-[11px]"
            style={{ color: "var(--color-yes)" }}
          >
            {success}
          </p>
        )}
        {error && (
          <p className="mt-1 text-[11px]" style={{ color: "var(--color-no)" }}>
            {error}
          </p>
        )}
      </div>
      <button
        type="button"
        onClick={handleReset}
        disabled={busy || success !== null}
        className="rounded-md px-4 py-2 text-[12px] font-semibold text-white transition-colors disabled:opacity-60"
        style={{ background: "var(--color-brand)" }}
      >
        {busy
          ? t("virtualResetBanner.busy")
          : success
            ? t("virtualResetBanner.done")
            : t("virtualResetBanner.cta")}
      </button>
    </div>
  );
}
